import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setupAPI } from '../services/api';
import styles from './Setup.module.css';

// Phases of the setup flow:
// VALIDATING — checking token on page load
// FORM       — token good, show password form
// SUBMITTING — submitting password
// SUCCESS    — done, prompt user to log in
// ERROR      — token invalid/expired/used (terminal state)
const PHASE = {
  VALIDATING: 'validating',
  FORM: 'form',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  ERROR: 'error',
};

// Translate the backend error code into a user-facing Greek message.
// Falls back to the server-provided error string, then to a generic message.
const errorMessageFor = (code, fallback) => {
  switch (code) {
    case 'INVALID_TOKEN':
      return 'Ο σύνδεσμος δεν είναι έγκυρος. Παρακαλώ επικοινωνήστε με τη διοίκηση.';
    case 'TOKEN_ALREADY_USED':
      return 'Ο σύνδεσμος έχει ήδη χρησιμοποιηθεί. Αν ξεχάσατε τον κωδικό σας, χρησιμοποιήστε την επαναφορά κωδικού.';
    case 'TOKEN_EXPIRED':
      return 'Ο σύνδεσμος έχει λήξει. Επικοινωνήστε με τη διοίκηση για νέο σύνδεσμο.';
    case 'MISSING_TOKEN':
      return 'Λείπει ο σύνδεσμος επιβεβαίωσης. Παρακαλώ ανοίξτε ξανά τον σύνδεσμο από το email.';
    default:
      return fallback || 'Παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.';
  }
};

// Password strength validation, mirrors backend rule:
// >= 8 chars, at least 1 lowercase, 1 uppercase, 1 digit
const validatePassword = (pw) => {
  if (!pw || pw.length < 8) return 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες';
  if (!/[a-z]/.test(pw)) return 'Ο κωδικός πρέπει να περιέχει πεζά γράμματα';
  if (!/[A-Z]/.test(pw)) return 'Ο κωδικός πρέπει να περιέχει κεφαλαία γράμματα';
  if (!/\d/.test(pw))    return 'Ο κωδικός πρέπει να περιέχει αριθμούς';
  return null;
};

export default function SetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [phase, setPhase]       = useState(PHASE.VALIDATING);
  const [adminInfo, setAdminInfo] = useState(null); // { email, firstName, lastName }
  const [errorMsg, setErrorMsg] = useState('');

  const [password, setPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [formError, setFormError]     = useState('');

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setPhase(PHASE.ERROR);
      setErrorMsg(errorMessageFor('MISSING_TOKEN'));
      return;
    }

    setupAPI.validate(token)
      .then(({ data }) => {
        setAdminInfo(data.data);
        setPhase(PHASE.FORM);
      })
      .catch((err) => {
        const code = err.response?.data?.code;
        const serverMsg = err.response?.data?.error;
        setErrorMsg(errorMessageFor(code, serverMsg));
        setPhase(PHASE.ERROR);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const pwErr = validatePassword(password);
    if (pwErr) {
      setFormError(pwErr);
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Οι κωδικοί δεν ταιριάζουν');
      return;
    }

    setPhase(PHASE.SUBMITTING);

    try {
      await setupAPI.complete(token, password);
      setPhase(PHASE.SUCCESS);
    } catch (err) {
      // If token expired/used between page load and submit, surface that
      // properly — the user might've sat on the page for hours.
      const code = err.response?.data?.code;
      if (code === 'INVALID_TOKEN' || code === 'TOKEN_ALREADY_USED' || code === 'TOKEN_EXPIRED') {
        setErrorMsg(errorMessageFor(code));
        setPhase(PHASE.ERROR);
        return;
      }
      // Validation errors (e.g. password too weak according to server) come
      // back as 400 with a generic message — show inline so user can fix.
      const serverMsg = err.response?.data?.error || 'Παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.';
      setFormError(serverMsg);
      setPhase(PHASE.FORM);
    }
  };

  // Right-pane content varies by phase
  const renderRightPane = () => {
    if (phase === PHASE.VALIDATING) {
      return (
        <div className={styles.formWrap}>
          <div className={styles.greeting}>Επιβεβαίωση...</div>
          <p className={styles.greetingSub}>Ελέγχουμε τον σύνδεσμό σας.</p>
          <div className={styles.spinner} />
        </div>
      );
    }

    if (phase === PHASE.ERROR) {
      return (
        <div className={styles.formWrap}>
          <div className={styles.greeting}>Πρόβλημα με τον σύνδεσμο</div>
          <div className={styles.errorBox}>⚠️ {errorMsg}</div>
          <button className={styles.btn} onClick={() => navigate('/login')}>
            Μετάβαση σε σύνδεση →
          </button>
        </div>
      );
    }

    if (phase === PHASE.SUCCESS) {
      return (
        <div className={styles.formWrap}>
          <div className={styles.successIcon}>✓</div>
          <div className={styles.greeting}>Έτοιμο!</div>
          <p className={styles.greetingSub}>
            Ο κωδικός σας ορίστηκε επιτυχώς. Μπορείτε τώρα να συνδεθείτε στην πύλη διαχείρισης.
          </p>
          <button className={styles.btn} onClick={() => navigate('/login')}>
            Σύνδεση →
          </button>
        </div>
      );
    }

    // FORM or SUBMITTING
    const isSubmitting = phase === PHASE.SUBMITTING;
    return (
      <div className={styles.formWrap}>
        <div className={styles.greeting}>
          Καλώς ήρθατε{adminInfo?.firstName ? `, ${adminInfo.firstName}` : ''}
        </div>
        <p className={styles.greetingSub}>
          {adminInfo?.email && <>Ορίζετε κωδικό για <strong>{adminInfo.email}</strong></>}
        </p>

        {formError && <div className={styles.errorBox}>⚠️ {formError}</div>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>Νέος κωδικός</label>
            <div className={styles.inputWrap}>
              <input
                type={showPass ? 'text' : 'password'}
                className={`${styles.input} ${styles.inputWithIcon}`}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => setShowPass(!showPass)}
                tabIndex={-1}
              >
                {showPass ? '🙈' : '👁'}
              </button>
            </div>
            <div className={styles.hint}>
              Τουλάχιστον 8 χαρακτήρες, με κεφαλαία, πεζά και αριθμούς.
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Επιβεβαίωση κωδικού</label>
            <input
              type={showPass ? 'text' : 'password'}
              className={styles.input}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className={styles.btn}
            disabled={!password || !confirmPassword || isSubmitting}
          >
            {isSubmitting ? 'Αποθήκευση...' : 'Ορισμός κωδικού →'}
          </button>
        </form>

        <p className={styles.privacyNote}>
          🔒 Κρυπτογράφηση 256-bit · Συμβατό με GDPR · Διακομιστές ΕΕ
        </p>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.left}>
        <div className={styles.leftContent}>
          <div className={styles.brand}>
            <div className={styles.logoCross} />
            <span className={styles.logoName}>Doc<span>Now</span></span>
          </div>
          <div className={styles.tagline}>
            Καλώς ήρθατε<br />στην ομάδα<br /><em>διαχείρισης</em>
          </div>
          <p className={styles.taglineSub}>
            Ο λογαριασμός σας στην πύλη διαχείρισης δημιουργήθηκε. Λίγα δευτερόλεπτα ακόμη και είστε έτοιμοι.
          </p>
          <div className={styles.steps}>
            <div className={styles.stepItem}>
              <div className={styles.stepDot}>1</div>
              <div className={styles.stepLabel}>Ορίστε κωδικό πρόσβασης</div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepDot}>2</div>
              <div className={styles.stepLabel}>Συνδεθείτε με 2FA</div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepDot}>3</div>
              <div className={styles.stepLabel}>Ξεκινήστε διαχείριση</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.right}>{renderRightPane()}</div>
    </div>
  );
}
